/**
 * i18next setup for RFQ Frontend.
 *
 * Resources are bundled (no HTTP backend) and split per language + namespace
 * under src/locales/{lang}/{ns}.json. The legacy translations.json structure
 * (top-level "common" and "components" objects) lives in the default
 * "translation" namespace, so existing callers like `t('common.loading')`
 * keep resolving through nested keys.
 *
 * Language is detected from localStorage key "app_language" first, then the
 * browser, with English as the final fallback. ICU formatting is enabled for
 * upcoming plural/select use cases.
 */
import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import ICU from 'i18next-icu';

import trTranslation from '../locales/tr/translation.json';
import enTranslation from '../locales/en/translation.json';
import deTranslation from '../locales/de/translation.json';

import trInbox from '../locales/tr/inbox.json';
import enInbox from '../locales/en/inbox.json';
import deInbox from '../locales/de/inbox.json';

import trCreateRequest from '../locales/tr/create-request.json';
import enCreateRequest from '../locales/en/create-request.json';
import deCreateRequest from '../locales/de/create-request.json';

import trRfqDetail from '../locales/tr/rfq-detail.json';
import enRfqDetail from '../locales/en/rfq-detail.json';
import deRfqDetail from '../locales/de/rfq-detail.json';


import trSuppliers from '../locales/tr/suppliers.json';
import enSuppliers from '../locales/en/suppliers.json';
import deSuppliers from '../locales/de/suppliers.json';

import trCommunication from '../locales/tr/communication.json';
import enCommunication from '../locales/en/communication.json';
import deCommunication from '../locales/de/communication.json';

import trSourcing from '../locales/tr/sourcing.json';
import enSourcing from '../locales/en/sourcing.json';
import deSourcing from '../locales/de/sourcing.json';

import trChangeSupplier from '../locales/tr/change-supplier.json';
import enChangeSupplier from '../locales/en/change-supplier.json';
import deChangeSupplier from '../locales/de/change-supplier.json';
import trSupplierLinks from '../locales/tr/supplier-links.json';
import enSupplierLinks from '../locales/en/supplier-links.json';
import deSupplierLinks from '../locales/de/supplier-links.json';

import trCatalogApprovals from '../locales/tr/catalog-approvals.json';
import enCatalogApprovals from '../locales/en/catalog-approvals.json';
import deCatalogApprovals from '../locales/de/catalog-approvals.json';

import trSettings from '../locales/tr/settings.json';
import enSettings from '../locales/en/settings.json';
import deSettings from '../locales/de/settings.json';

import trErrors from '../locales/tr/errors.json';
import enErrors from '../locales/en/errors.json';
import deErrors from '../locales/de/errors.json';

import trSidebar from '../locales/tr/sidebar.json';
import enSidebar from '../locales/en/sidebar.json';
import deSidebar from '../locales/de/sidebar.json';

import trScraper from '../locales/tr/scraper.json';
import enScraper from '../locales/en/scraper.json';
import deScraper from '../locales/de/scraper.json';

import trManualProduct from '../locales/tr/manual-product.json';
import enManualProduct from '../locales/en/manual-product.json';
import deManualProduct from '../locales/de/manual-product.json';

import trClosedRfq from '../locales/tr/closed-rfq.json';
import enClosedRfq from '../locales/en/closed-rfq.json';
import deClosedRfq from '../locales/de/closed-rfq.json';

import trExport from '../locales/tr/export.json';
import enExport from '../locales/en/export.json';
import deExport from '../locales/de/export.json';

import trCommDashboard from '../locales/tr/comm-dashboard.json';
import enCommDashboard from '../locales/en/comm-dashboard.json';
import deCommDashboard from '../locales/de/comm-dashboard.json';
import trMailDashboard from '../locales/tr/mail-dashboard.json';
import enMailDashboard from '../locales/en/mail-dashboard.json';
import deMailDashboard from '../locales/de/mail-dashboard.json';

import trAgentHealth from '../locales/tr/agent-health.json';
import enAgentHealth from '../locales/en/agent-health.json';
import deAgentHealth from '../locales/de/agent-health.json';

import trDocuments from '../locales/tr/documents.json';
import enDocuments from '../locales/en/documents.json';
import deDocuments from '../locales/de/documents.json';

import trToast from '../locales/tr/toast.json';
import enToast from '../locales/en/toast.json';
import deToast from '../locales/de/toast.json';

import trCatalogWeb from '../locales/tr/catalog-web.json';
import enCatalogWeb from '../locales/en/catalog-web.json';
import deCatalogWeb from '../locales/de/catalog-web.json';

import trCatalogControl from '../locales/tr/catalog-control.json';
import enCatalogControl from '../locales/en/catalog-control.json';
import deCatalogControl from '../locales/de/catalog-control.json';

import trCatalogJobs from '../locales/tr/catalog-jobs.json';
import enCatalogJobs from '../locales/en/catalog-jobs.json';
import deCatalogJobs from '../locales/de/catalog-jobs.json';

import trWebCheck from '../locales/tr/web-check.json';
import enWebCheck from '../locales/en/web-check.json';
import deWebCheck from '../locales/de/web-check.json';

import trContractPo from '../locales/tr/contract-po.json';
import enContractPo from '../locales/en/contract-po.json';
import deContractPo from '../locales/de/contract-po.json';

export const SUPPORTED_LANGUAGES = ['tr', 'en', 'de'] as const;
export type SupportedLanguage = (typeof SUPPORTED_LANGUAGES)[number];

export const LANGUAGE_STORAGE_KEY = 'app_language';

export const NAMESPACES = [
  'translation',
  'inbox',
  'create-request',
  'rfq-detail',
  'suppliers',
  'communication',
  'sourcing',
  'change-supplier',
  'supplier-links',
  'catalog-approvals',
  'settings',
  'errors',
  'sidebar',
  'scraper',
  'manual-product',
  'closed-rfq',
  'export',
  'comm-dashboard',
  'mail-dashboard',
  'agent-health',
  'documents',
  'toast',
  'catalog-web',
  'catalog-control',
  'catalog-jobs',
  'web-check',
  'contract-po',
] as const;

const resources = {
  tr: {
    translation: trTranslation,
    inbox: trInbox,
    'create-request': trCreateRequest,
    'rfq-detail': trRfqDetail,
    suppliers: trSuppliers,
    communication: trCommunication,
    sourcing: trSourcing,
    'change-supplier': trChangeSupplier,
    'supplier-links': trSupplierLinks,
    'catalog-approvals': trCatalogApprovals,
    settings: trSettings,
    errors: trErrors,
    sidebar: trSidebar,
    scraper: trScraper,
    'manual-product': trManualProduct,
    'closed-rfq': trClosedRfq,
    export: trExport,
    'comm-dashboard': trCommDashboard,
    'mail-dashboard': trMailDashboard,
    'agent-health': trAgentHealth,
    documents: trDocuments,
    toast: trToast,
    'catalog-web': trCatalogWeb,
    'catalog-control': trCatalogControl,
    'catalog-jobs': trCatalogJobs,
    'web-check': trWebCheck,
    'contract-po': trContractPo,
  },
  en: {
    translation: enTranslation,
    inbox: enInbox,
    'create-request': enCreateRequest,
    'rfq-detail': enRfqDetail,
    suppliers: enSuppliers,
    communication: enCommunication,
    sourcing: enSourcing,
    'change-supplier': enChangeSupplier,
    'supplier-links': enSupplierLinks,
    'catalog-approvals': enCatalogApprovals,
    settings: enSettings,
    errors: enErrors,
    sidebar: enSidebar,
    scraper: enScraper,
    'manual-product': enManualProduct,
    'closed-rfq': enClosedRfq,
    export: enExport,
    'comm-dashboard': enCommDashboard,
    'mail-dashboard': enMailDashboard,
    'agent-health': enAgentHealth,
    documents: enDocuments,
    toast: enToast,
    'catalog-web': enCatalogWeb,
    'catalog-control': enCatalogControl,
    'catalog-jobs': enCatalogJobs,
    'web-check': enWebCheck,
    'contract-po': enContractPo,
  },
  de: {
    translation: deTranslation,
    inbox: deInbox,
    'create-request': deCreateRequest,
    'rfq-detail': deRfqDetail,
    suppliers: deSuppliers,
    communication: deCommunication,
    sourcing: deSourcing,
    'change-supplier': deChangeSupplier,
    'supplier-links': deSupplierLinks,
    'catalog-approvals': deCatalogApprovals,
    settings: deSettings,
    errors: deErrors,
    sidebar: deSidebar,
    scraper: deScraper,
    'manual-product': deManualProduct,
    'closed-rfq': deClosedRfq,
    export: deExport,
    'comm-dashboard': deCommDashboard,
    'mail-dashboard': deMailDashboard,
    'agent-health': deAgentHealth,
    documents: deDocuments,
    toast: deToast,
    'catalog-web': deCatalogWeb,
    'catalog-control': deCatalogControl,
    'catalog-jobs': deCatalogJobs,
    'web-check': deWebCheck,
    'contract-po': deContractPo,
  },
};

if (ICU) i18n.use(ICU);
if (LanguageDetector) i18n.use(LanguageDetector);
if (initReactI18next) i18n.use(initReactI18next);

i18n.init({
  resources,
  supportedLngs: SUPPORTED_LANGUAGES as unknown as string[],
  fallbackLng: 'en',
  defaultNS: 'translation',
  ns: NAMESPACES as unknown as string[],
  nsSeparator: ':',
  keySeparator: '.',
  interpolation: {
    escapeValue: false,
  },
  detection: {
      // Shared language across all *.gordiananalytics.com frontends (RFQ,
      // portal, scraper) via a parent-domain cookie. Change it in one app →
      // every app reads the same cookie on next load. Cookie first, then the
      // legacy localStorage, then the browser. On localhost cookieDomain is
      // undefined (host-only cookie) so dev still works.
      order: ['cookie', 'localStorage', 'navigator'],
      lookupCookie: LANGUAGE_STORAGE_KEY,
      lookupLocalStorage: LANGUAGE_STORAGE_KEY,
      caches: ['cookie', 'localStorage'],
      cookieDomain:
        typeof window !== 'undefined' &&
        window.location.hostname.endsWith('gordiananalytics.com')
          ? '.gordiananalytics.com'
          : undefined,
      cookieMinutes: 525600, // 1 year
    },
    react: {
      useSuspense: false,
    },
    returnNull: false,
    returnEmptyString: false,
    saveMissing: process.env.NODE_ENV === 'development',
    missingKeyHandler: (lng, ns, key) => {
      if (process.env.NODE_ENV === 'development') {
        // eslint-disable-next-line no-console
        console.warn(`[i18n] missing key "${key}" in ${lng}/${ns}`);
      }
    },
  });

export default i18n;
